import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Button, Checkbox, IconButton, LinearProgress, Stack, Typography} from '@mui/joy';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ChecklistRounded from '@mui/icons-material/ChecklistRounded';
import {runArchive} from '../../home_media_ui/archive';

export const MAX_ARCHIVE_MESSAGES = 500;
export const hasArchiveMedia = message => Number.isSafeInteger(Number(message?.id)) && Number(message.id) > 0
  && message?.type_sender !== 'system' && !message?.is_deleted && !message?.deleted && Array.isArray(message?.attachments) && message.attachments.length > 0;
const initial = scope => ({scope, mode: false, ids: []});

export function useChatArchive({apiURL, chatId, token, audience = 'staff', messages, enabled = true}) {
  const allowed = Boolean(enabled && token && chatId);
  const scope = `${audience}:${chatId || ''}:${token || ''}:${enabled}`;
  const [selection, setSelection] = useState(() => initial(scope));
  const [job, setJob] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const controller = useRef(null), generation = useRef(0);
  const eligible = useMemo(() => messages.filter(hasArchiveMedia), [messages]);
  const ids = selection.scope === scope ? selection.ids : [];
  const mode = Boolean(enabled && selection.scope === scope && selection.mode);
  const eligibleIds = useMemo(() => new Set(eligible.map(message => String(message.id))), [eligible]);
  useEffect(() => {
    generation.current += 1; controller.current?.abort(); setSelection(initial(scope)); setBusy(false); setJob(null); setError('');
    return () => {generation.current += 1; controller.current?.abort();};
  }, [scope]);
  useEffect(() => {
    setSelection(previous => {
      if (previous.scope !== scope) return previous;
      const next = previous.ids.filter(id => eligibleIds.has(id));
      return next.length === previous.ids.length ? previous : {...previous, ids: next};
    });
  }, [scope, eligibleIds]);
  const selected = useMemo(() => new Set(ids), [ids]);
  const count = eligible.filter(message => selected.has(String(message.id))).reduce((sum, message) => sum + message.attachments.length, 0);
  const begin = () => {if (enabled) {setError(''); setSelection(previous => previous.scope === scope ? {...previous, mode: true} : {...initial(scope), mode: true});}};
  const toggle = useCallback(message => {
    if (!enabled || !hasArchiveMedia(message) || busy) return;
    const id = String(message.id);
    setSelection(previous => {
      const current = previous.scope === scope ? previous : initial(scope);
      if (current.ids.includes(id)) return {...current, mode: true, ids: current.ids.filter(value => value !== id)};
      if (current.ids.length >= MAX_ARCHIVE_MESSAGES) return {...current, mode: true};
      return {...current, mode: true, ids: [...current.ids, id]};
    });
    setError('');
  }, [scope, enabled, busy]);
  const all = () => {
    if (busy) return;
    const limited = eligible.slice(0, MAX_ARCHIVE_MESSAGES).map(message => String(message.id));
    setSelection({scope, mode: true, ids: limited.every(id => selected.has(id)) ? [] : limited}); setError('');
  };
  const close = () => {controller.current?.abort(); setSelection(initial(scope)); setError(''); setJob(null); setBusy(false); generation.current += 1;};
  async function download() {
    if (!enabled || busy || !ids.length || !token || !chatId) return;
    const requestGeneration = generation.current;
    const abort = new AbortController(); controller.current = abort; setBusy(true); setError(''); setJob(null);
    try {
      await runArchive(apiURL, token, {scope: 'chat', chat_id: Number(chatId), message_ids: ids.map(Number)}, {
        audience, signal: abort.signal, onUpdate: value => {if (requestGeneration === generation.current) setJob(value);},
      });
      if (requestGeneration === generation.current && !abort.signal.aborted) setJob({status: 'downloaded'});
    } catch (failure) {
      if (requestGeneration !== generation.current || abort.signal.aborted) return;
      setError(failure.message || 'Не удалось подготовить архив вложений.');
      if ([401, 403, 404].includes(failure.status)) setSelection(initial(scope));
    } finally {if (requestGeneration === generation.current) setBusy(false);}
  }
  const capture = (event, message) => {
    if (!mode || !hasArchiveMedia(message) || event.target.closest?.('[data-archive-checkbox]')) return;
    event.preventDefault(); event.stopPropagation(); toggle(message);
  };
  return {enabled: allowed, mode, ids, selected, count, busy, error, job, eligible, begin, toggle, all, close, download, capture};
}

export function ChatArchiveCheckbox({message, archive}) {
  if (!archive.mode || !hasArchiveMedia(message)) return null;
  return <Box data-archive-checkbox sx={{alignSelf: 'center', flexShrink: 0, px: 0.25}}>
    <Checkbox size="sm" checked={archive.selected.has(String(message.id))} disabled={archive.busy}
      onChange={() => archive.toggle(message)} slotProps={{input: {'aria-label': `Выбрать вложения сообщения ${message.id}`}}}/>
  </Box>;
}

export default function ChatArchiveSelection({archive}) {
  if (!archive.enabled || (!archive.eligible.length && !archive.mode && !archive.error)) return null;
  return <Box sx={{flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider', px: {xs: 1.25, md: 2}, py: 0.75,
    bgcolor: archive.mode ? 'primary.softBg' : 'background.body'}}>
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{alignItems: 'center'}}>
      {!archive.mode ? <Button size="sm" variant="plain" color="neutral" startDecorator={<ChecklistRounded/>} onClick={archive.begin}>Выбрать вложения</Button> : <>
        <Typography level="body-sm" sx={{fontWeight: 650, flex: '1 1 140px'}}>Выбрано: {archive.ids.length} · файлов: {archive.count}</Typography>
        <Button size="sm" variant="plain" disabled={archive.busy} onClick={archive.all}>Все загруженные</Button>
        <Button size="sm" variant="solid" startDecorator={<DownloadRounded/>} loading={archive.busy}
          disabled={!archive.ids.length} onClick={archive.download}>Скачать ZIP</Button>
        <IconButton size="sm" variant="plain" color="neutral" aria-label={archive.busy ? 'Прекратить ожидание архива' : 'Снять выбор вложений'} onClick={archive.close}><CloseRounded/></IconButton>
      </>}
    </Stack>
    {archive.mode && <Typography level="body-xs" sx={{mt: 0.5, color: 'text.tertiary'}}>Отметьте сообщения с файлами или фото. Альбом скачивается целиком. До 500 сообщений.</Typography>}
    {archive.busy && <><LinearProgress size="sm" sx={{mt: 0.75}}/><Typography level="body-xs" role="status" sx={{mt: 0.5}}>Готовим архив вложений… Можно продолжать читать чат.</Typography></>}
    {!archive.busy && archive.job?.status === 'downloaded' && <Typography level="body-xs" color="success" role="status" sx={{mt: 0.5}}>Архив готов. Загрузка передана браузеру.</Typography>}
    {archive.error && <Alert size="sm" color="warning" role="alert" sx={{mt: 0.75, overflowWrap: 'anywhere'}}>{archive.error}</Alert>}
  </Box>;
}
